import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, RefreshCw, Tag, Package, FileSpreadsheet, Download,
  Upload, Zap, LayoutGrid, List, Pencil, Power, History, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { storeApi, formatApiError } from '../lib/api';
import { CATEGORY_LABELS, PRODUCT_STATUS_CONFIG, formatPrice, formatDateIt } from '../components/store/storeConstants';
import { ProductCardAgent } from '../components/store/ProductCardAgent';
import { ProductFormDialog } from '../components/store/ProductFormDialog';
import { ExcelImportDialog } from '../components/store/ExcelImportDialog';

const EMPTY_FILTERS = { search: '', category: '', brand: '', product_line: '', status: '', has_promo: false, promo_validity: '' };

const StatCard = ({ label, value, color = 'text-slate-900', testId }) => (
  <Card>
    <CardContent className="p-4 text-center">
      <p className={`text-2xl font-bold ${color}`} data-testid={testId}>{value ?? '-'}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </CardContent>
  </Card>
);

export default function StoreProductsAdminPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('catalogo');

  // Dialogs
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Audit
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const [metaRes, statsRes] = await Promise.all([storeApi.getProductsMeta(), storeApi.getCatalogStats()]);
      setMeta(metaRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading meta:', error);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.product_line) params.product_line = filters.product_line;
      if (filters.status) params.status = filters.status;
      if (filters.has_promo) params.has_promo = true;
      if (filters.promo_validity) params.promo_validity = filters.promo_validity;
      const response = await storeApi.getProducts(params);
      setProducts(response.data.items || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const response = await storeApi.getAuditLog({ limit: 50 });
      setAuditEntries(response.data.items || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { if (activeTab === 'audit') loadAudit(); }, [activeTab, loadAudit]);

  const refreshAll = () => { loadProducts(); loadMeta(); };

  const handleEdit = (product) => { setEditingProduct(product); setShowProductForm(true); };
  const handleNew = () => { setEditingProduct(null); setShowProductForm(true); };

  const handleToggleActive = async (product) => {
    const newStatus = ['ATTIVO', 'PROMO_ATTIVA', 'PROMO_SCADUTA'].includes(product.status) ? 'INATTIVO' : 'ATTIVO';
    try {
      await storeApi.updateProduct(product.id, { status: newStatus });
      toast.success(`Prodotto ${newStatus === 'ATTIVO' ? 'attivato' : 'disattivato'}`);
      refreshAll();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDownload = async (type) => {
    setDownloading(true);
    try {
      if (type === 'template') await storeApi.downloadExcelTemplate();
      else await storeApi.exportCatalog();
      toast.success('File scaricato');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDownloading(false);
    }
  };

  const filterSelect = (label, value, onChange, options, testId, renderLabel = (v) => v) => (
    <div className="w-[160px]">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value || 'all'} onValueChange={(v) => onChange(v === 'all' ? '' : v)}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{renderLabel(o)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="store-products-admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-blue-600" />
            Prodotti Store
          </h1>
          <p className="text-muted-foreground mt-1">Catalogo commerciale HVAC: prodotti, prezzi, promo e disponibilità</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleDownload('template')} disabled={downloading} data-testid="download-template-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Modello Excel
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-excel-btn">
            <Upload className="h-4 w-4 mr-2" /> Importa Excel
          </Button>
          <Button variant="outline" onClick={() => handleDownload('export')} disabled={downloading} data-testid="export-catalog-btn">
            <Download className="h-4 w-4 mr-2" /> Esporta Catalogo
          </Button>
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => navigate('/store/modifiche-rapide')} data-testid="bulk-edit-btn">
            <Zap className="h-4 w-4 mr-2" /> Modifiche Rapide
          </Button>
          <Button onClick={handleNew} data-testid="new-product-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo Prodotto
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatCard label="Prodotti a catalogo" value={stats?.total_products} testId="stat-total" />
        <StatCard label="Attivi" value={stats?.total_active} color="text-green-600" testId="stat-active" />
        <StatCard label="Con promo" value={stats?.total_with_promo} color="text-blue-600" testId="stat-promo" />
        <StatCard label="Promo scadute" value={stats?.total_promo_expired} color="text-amber-600" testId="stat-expired" />
        <StatCard label="Da verificare" value={stats?.total_da_verificare} color="text-orange-600" testId="stat-verify" />
        <StatCard label="Marche" value={stats?.brands_count} testId="stat-brands" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="catalogo" data-testid="tab-catalogo-admin">Catalogo</TabsTrigger>
          <TabsTrigger value="audit" className="gap-1" data-testid="tab-audit"><History className="h-4 w-4" /> Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo" className="space-y-4 mt-4">
          {/* Filtri */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-xs text-muted-foreground">Ricerca rapida</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Codice, marca, modello, linea..."
                      value={filters.search}
                      onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                      className="pl-10"
                      data-testid="admin-search-input"
                    />
                  </div>
                </div>
                {filterSelect('Categoria', filters.category, (v) => setFilters((f) => ({ ...f, category: v })), meta?.categories || [], 'filter-category', (c) => CATEGORY_LABELS[c] || c)}
                {filterSelect('Marca', filters.brand, (v) => setFilters((f) => ({ ...f, brand: v })), meta?.brands || [], 'filter-brand')}
                {filterSelect('Linea', filters.product_line, (v) => setFilters((f) => ({ ...f, product_line: v })), meta?.product_lines || [], 'filter-line')}
                {filterSelect('Stato', filters.status, (v) => setFilters((f) => ({ ...f, status: v })), meta?.statuses || [], 'filter-status', (s) => PRODUCT_STATUS_CONFIG[s]?.label || s)}
                {filterSelect('Validità promo', filters.promo_validity, (v) => setFilters((f) => ({ ...f, promo_validity: v })), ['valid', 'expired'], 'filter-promo-validity', (v) => (v === 'valid' ? 'Promo valide' : 'Promo scadute'))}
                <Button
                  variant={filters.has_promo ? 'default' : 'outline'}
                  onClick={() => setFilters((f) => ({ ...f, has_promo: !f.has_promo }))}
                  data-testid="filter-has-promo"
                >
                  <Tag className="h-4 w-4 mr-1" /> Con Promo
                </Button>
                <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="reset-filters-btn">
                  <RefreshCw className="h-4 w-4" />
                </Button>
                <div className="flex border rounded-lg overflow-hidden ml-auto">
                  <Button variant={viewMode === 'table' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('table')} data-testid="view-table-btn">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button variant={viewMode === 'cards' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('cards')} data-testid="view-cards-btn">
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista prodotti */}
          {loading ? (
            <Card className="p-8"><div className="animate-pulse space-y-3">{[...Array(6)].map((_, i) => <div key={`skeleton-row-${i}`} className="h-10 bg-gray-100 rounded" />)}</div></Card>
          ) : products.length === 0 ? (
            <Card className="p-10 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="font-medium">Nessun prodotto trovato</p>
              <p className="text-sm text-muted-foreground">Modifica i filtri o importa il catalogo da Excel</p>
            </Card>
          ) : viewMode === 'table' ? (
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="products-table">
                  <thead className="bg-slate-50 border-b">
                    <tr className="text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-3">Codice</th>
                      <th className="px-3 py-3">Categoria</th>
                      <th className="px-3 py-3">Marca / Linea</th>
                      <th className="px-3 py-3">Modello</th>
                      <th className="px-3 py-3">Potenza/Cap.</th>
                      <th className="px-3 py-3 text-right">Prezzo Base</th>
                      <th className="px-3 py-3 text-right">Prezzo Promo</th>
                      <th className="px-3 py-3 text-center">IVA</th>
                      <th className="px-3 py-3">Promo</th>
                      <th className="px-3 py-3">Validità</th>
                      <th className="px-3 py-3">Stato</th>
                      <th className="px-3 py-3 text-center">Agenti</th>
                      <th className="px-3 py-3 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => {
                      const statusCfg = PRODUCT_STATUS_CONFIG[p.effective_status || p.status] || {};
                      return (
                        <tr key={p.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => handleEdit(p)} data-testid={`product-row-${p.product_code}`}>
                          <td className="px-3 py-2.5 font-mono text-xs">{p.product_code}</td>
                          <td className="px-3 py-2.5"><Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[p.category] || p.category}</Badge></td>
                          <td className="px-3 py-2.5">
                            <p className="font-medium">{p.brand}</p>
                            <p className="text-xs text-muted-foreground">{p.product_line || '-'}</p>
                          </td>
                          <td className="px-3 py-2.5 max-w-[200px] truncate">{p.model}</td>
                          <td className="px-3 py-2.5 text-xs">{p.power_label || p.capacity_label || '-'}</td>
                          <td className="px-3 py-2.5 text-right font-medium">{formatPrice(p.base_price)}</td>
                          <td className="px-3 py-2.5 text-right text-blue-600">{p.promo_price ? formatPrice(p.promo_price) : '-'}</td>
                          <td className="px-3 py-2.5 text-center text-xs">{p.vat_rate ?? 10}%</td>
                          <td className="px-3 py-2.5 text-xs">{p.promo_code || (p.voucher_amount > 0 ? 'Voucher' : p.zonal_discount > 0 ? 'Sconto Zonale' : '-')}</td>
                          <td className="px-3 py-2.5 text-xs whitespace-nowrap">{p.promo_valid_to ? `fino al ${formatDateIt(p.promo_valid_to)}` : '-'}</td>
                          <td className="px-3 py-2.5"><Badge className={`text-xs border ${statusCfg.color || ''}`}>{statusCfg.label || p.status}</Badge></td>
                          <td className="px-3 py-2.5 text-center">
                            {p.visible_to_agents === false ? <AlertTriangle className="h-4 w-4 text-amber-500 mx-auto" /> : <span className="text-green-600 text-xs">✓</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(p)} data-testid={`edit-product-${p.product_code}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => handleToggleActive(p)} title="Attiva/Disattiva" data-testid={`toggle-product-${p.product_code}`}>
                              <Power className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {products.map((p) => (
                <ProductCardAgent key={p.id} product={p} onSelect={() => handleEdit(p)} buttonLabel="Modifica Scheda" showStatus />
              ))}
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center">{products.length} prodotti</p>
        </TabsContent>

        {/* Audit Log */}
        <TabsContent value="audit" className="mt-4">
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="px-3 py-3">Data</th>
                    <th className="px-3 py-3">Utente</th>
                    <th className="px-3 py-3">Azione</th>
                    <th className="px-3 py-3">Prodotto</th>
                    <th className="px-3 py-3">Modifiche</th>
                    <th className="px-3 py-3">Batch</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLoading ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>
                  ) : auditEntries.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Nessuna voce di audit</td></tr>
                  ) : auditEntries.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(entry.timestamp).toLocaleString('it-IT')}</td>
                      <td className="px-3 py-2 text-xs">{entry.user_name || entry.user_email}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{entry.action}</Badge></td>
                      <td className="px-3 py-2 font-mono text-xs">{entry.product_code || '-'}</td>
                      <td className="px-3 py-2 text-xs max-w-[400px]">
                        {entry.details && <p className="text-muted-foreground">{entry.details}</p>}
                        {(entry.changes || []).slice(0, 4).map((c, i) => (
                          <p key={`${c.field || 'change'}-${i}`}>{c.field}: <span className="text-red-500">{String(c.old ?? '-')}</span> → <span className="text-green-600">{String(c.new ?? '-')}</span></p>
                        ))}
                        {(entry.changes || []).length > 4 && <p className="text-muted-foreground">+{entry.changes.length - 4} altre modifiche</p>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{entry.batch_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProductFormDialog
        open={showProductForm}
        onOpenChange={setShowProductForm}
        product={editingProduct}
        meta={meta}
        onSaved={refreshAll}
      />
      <ExcelImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onImported={refreshAll}
      />
    </div>
  );
}
